import { trackEvent } from './tracker';

export const AnalyticsEvents = {
  nav_click: 'nav_click',
  nav_external_click: 'nav_external_click',
  logout: 'logout',
  api_key_copy: 'api_key_copy',
  query_run: 'query_run',
  query_load_more: 'query_load_more',
  query_view_mode_change: 'query_view_mode_change',
  query_dense_toggle: 'query_dense_toggle',
  query_show_index_toggle: 'query_show_index_toggle',
  query_date_preset: 'query_date_preset',
  query_date_modal_open: 'query_date_modal_open',
  query_date_modal_close: 'query_date_modal_close',
  export_initiated: 'export_initiated',
  export_confirm_opened: 'export_confirm_opened',
  export_confirmed: 'export_confirmed',
  export_confirm_canceled: 'export_confirm_canceled',
  export_format_changed: 'export_format_changed',
  credits_add_click: 'credits_add_click',
  credits_checkout_start: 'credits_checkout_start',
  credits_modal_close: 'credits_modal_close',
  credits_quick_pick: 'credits_quick_pick',
  organization_create_confirm_open: 'organization_create_confirm_open',
  organization_created: 'organization_created',
  organization_join_code_submit: 'organization_join_code_submit',
  invite_viewed: 'invite_viewed',
  invite_accept: 'invite_accept',
  invite_decline: 'invite_decline',
  members_invite_opened: 'members_invite_opened',
  members_invite_sent: 'members_invite_sent',
  members_role_updated: 'members_role_updated',
  members_removed: 'members_removed',
  satellite_task_submitted: 'satellite_task_submitted',
  satellite_task_refresh: 'satellite_task_refresh',
  satellite_admin_refresh: 'satellite_admin_refresh',
  satellite_copy_coords: 'satellite_copy_coords',
  satellite_open_maps: 'satellite_open_maps',
  satellite_clear_polygon: 'satellite_clear_polygon',
  datapoint_pricing_saved: 'datapoint_pricing_saved',
} as const;

export type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

export const trackNavClick = (params: {
  destination_path: string;
  nav_label?: string;
  nav_location?: string;
}) => trackEvent(AnalyticsEvents.nav_click, params);

export const trackNavExternalClick = (params: { external_url: string; nav_label?: string }) =>
  trackEvent(AnalyticsEvents.nav_external_click, params);

export const trackLogout = (params?: { logout_location?: string }) =>
  trackEvent(AnalyticsEvents.logout, params);

export const trackApiKeyCopy = (params?: { source?: string }) =>
  trackEvent(AnalyticsEvents.api_key_copy, params);

export const trackQueryRun = (params: {
  category?: string;
  source?: string;
  type?: string;
  locality?: string;
  date_range_days?: number;
  view_mode?: string;
  trigger?: string;
}) => trackEvent(AnalyticsEvents.query_run, params);

export const trackQueryLoadMore = (params?: { current_count?: number }) =>
  trackEvent(AnalyticsEvents.query_load_more, params);

export const trackQueryViewModeChange = (params: { view_mode: string }) =>
  trackEvent(AnalyticsEvents.query_view_mode_change, params);

export const trackQueryDenseToggle = (params: { enabled: boolean }) =>
  trackEvent(AnalyticsEvents.query_dense_toggle, params);

export const trackQueryShowIndexToggle = (params: { enabled: boolean }) =>
  trackEvent(AnalyticsEvents.query_show_index_toggle, params);

export const trackQueryDatePreset = (params: { preset: string; range_days?: number }) =>
  trackEvent(AnalyticsEvents.query_date_preset, params);

export const trackQueryDateModalOpen = () => trackEvent(AnalyticsEvents.query_date_modal_open);

export const trackQueryDateModalClose = () => trackEvent(AnalyticsEvents.query_date_modal_close);

export const trackExportInitiated = (params: {
  scope: string;
  format: string;
  record_count?: number;
  category?: string;
  locality?: string;
}) => trackEvent(AnalyticsEvents.export_initiated, params);

export const trackExportConfirmOpened = (params: { scope: string; format: string; record_count?: number }) =>
  trackEvent(AnalyticsEvents.export_confirm_opened, params);

export const trackExportConfirmed = (params: { scope: string; format: string; record_count?: number }) =>
  trackEvent(AnalyticsEvents.export_confirmed, params);

export const trackExportConfirmCanceled = (params: { scope: string; format: string }) =>
  trackEvent(AnalyticsEvents.export_confirm_canceled, params);

export const trackExportFormatChanged = (params: { format: string }) =>
  trackEvent(AnalyticsEvents.export_format_changed, params);

export const trackCreditsAddClick = (params: { amount_eur: number }) =>
  trackEvent(AnalyticsEvents.credits_add_click, params);

export const trackCreditsCheckoutStart = (params: { amount_eur: number }) =>
  trackEvent(AnalyticsEvents.credits_checkout_start, params);

export const trackCreditsModalClose = (params: { amount_eur: number }) =>
  trackEvent(AnalyticsEvents.credits_modal_close, params);

export const trackCreditsQuickPick = (params: { amount_eur: number }) =>
  trackEvent(AnalyticsEvents.credits_quick_pick, params);

export const trackOrganizationCreateConfirmOpen = (params: { name_length: number }) =>
  trackEvent(AnalyticsEvents.organization_create_confirm_open, params);

export const trackOrganizationCreated = () => trackEvent(AnalyticsEvents.organization_created);

export const trackOrganizationJoinCodeSubmit = (params: { code_length: number }) =>
  trackEvent(AnalyticsEvents.organization_join_code_submit, params);

export const trackInviteViewed = (params: { has_mismatch: boolean; role?: string }) =>
  trackEvent(AnalyticsEvents.invite_viewed, params);

export const trackInviteAccept = (params?: { has_mismatch?: boolean }) =>
  trackEvent(AnalyticsEvents.invite_accept, params);

export const trackInviteDecline = () => trackEvent(AnalyticsEvents.invite_decline);

export const trackMembersInviteOpened = (params?: { source?: string }) =>
  trackEvent(AnalyticsEvents.members_invite_opened, params);

export const trackMembersInviteSent = (params: { role: string }) =>
  trackEvent(AnalyticsEvents.members_invite_sent, params);

export const trackMembersRoleUpdated = (params: { role: string }) =>
  trackEvent(AnalyticsEvents.members_role_updated, params);

export const trackMembersRemoved = (params: { role: string; status?: string }) =>
  trackEvent(AnalyticsEvents.members_removed, params);

export const trackSatelliteTaskSubmitted = (params: { provider: string; has_datetime: boolean }) =>
  trackEvent(AnalyticsEvents.satellite_task_submitted, params);

export const trackSatelliteTaskRefresh = () => trackEvent(AnalyticsEvents.satellite_task_refresh);

export const trackSatelliteAdminRefresh = () => trackEvent(AnalyticsEvents.satellite_admin_refresh);

export const trackSatelliteCopyCoords = () => trackEvent(AnalyticsEvents.satellite_copy_coords);

export const trackSatelliteOpenMaps = () => trackEvent(AnalyticsEvents.satellite_open_maps);

export const trackSatelliteClearPolygon = () => trackEvent(AnalyticsEvents.satellite_clear_polygon);

export const trackDatapointPricingSaved = () => trackEvent(AnalyticsEvents.datapoint_pricing_saved);
